import { IS_LOCAL } from '../../../../constants'
import { ApiService } from 'shared/http/v2/http.mixin'
import {
  fakeContainers,
  fakeEmptyContainers,
  fakeFindCargoByOrderNumber,
  fakeWarehouseStock,
} from './fake'
import {
  buildWarehouseFilters,
  WarehouseStockResponse,
  ListByWarehouseResponse,
  TWarehouseFilters,
  TCargo,
  ListEmptyContainersResponse,
} from './types'
import { HttpStatusCode } from 'axios'

export class ContainersAPI extends ApiService({ basePath: '/', apiVersion: 'v2' }) {
  public static async listByWarehouse(
    warehouseId: number,
    filters?: TWarehouseFilters
  ): Promise<ListByWarehouseResponse> {
    if (IS_LOCAL) {
      return fakeContainers(filters)
    }

    const resp = await this.http.get<ListByWarehouseResponse>(`warehouses/${warehouseId}/cargo`, {
      query: buildWarehouseFilters(filters),
    })

    if (resp.status === HttpStatusCode.NotFound) {
      return []
    }

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }

    return resp.data
  }

  public static async listEmptyContainers(
    warehouseId: number
  ): Promise<ListEmptyContainersResponse> {
    if (IS_LOCAL) {
      return fakeEmptyContainers()
    }

    const resp = await this.http.get<ListEmptyContainersResponse>(
      `warehouses/${warehouseId}/containers/empty`
    )

    if (resp.status === HttpStatusCode.NotFound) {
      return []
    }

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }

    return resp.data
  }

  public static async transferCargo(
    warehouseId: number,
    cargoId: string,
    containerIds: string[],
    comment?: string
  ): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.post('cargo/transfer', {
      body: { cargoId, containerIds, warehouseId, comment },
    })

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }
  }

  public static async createCargo(
    serialNumber: number,
    warehouseId: number,
    containers: Record<string, string>[]
  ): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.post('orders/cargo/create', {
      body: { serialNumber, warehouseId, containers },
    })

    if (resp.status === 200) return

    switch (resp.status) {
      case HttpStatusCode.BadRequest:
        throw new Error('Переданы некорректные данные')
      case HttpStatusCode.NotFound:
        throw new Error('Заказ или один из контейнеров не найден')
      case HttpStatusCode.Conflict:
        throw new Error('Контейнер уже находится в другом грузе')
      default:
        throw new Error(resp.error)
    }
  }

  public static async transferContainers(
    warehouseId: number,
    containerIds: string[]
  ): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.post('containers/transfer', {
      body: { containerIds, warehouseId },
    })

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }
  }

  public static async findCargoByOrderNumber(orderNumber: string): Promise<TCargo[]> {
    if (IS_LOCAL) return fakeFindCargoByOrderNumber(orderNumber)

    const resp = await this.http.get<TCargo[]>(`orders/${encodeURIComponent(orderNumber)}/cargo`)

    if (resp.status === HttpStatusCode.NotFound) {
      return []
    }

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }

    return resp.data
  }

  public static async inspect(cargoId: string, containerIds: string[]): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.post('cargo/inventory', {
      body: { cargoId, containerIds },
    })

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }
  }

  public static async warehouseStock(warehouseId: number): Promise<WarehouseStockResponse> {
    if (IS_LOCAL) return fakeWarehouseStock()

    const resp = await this.http.get<WarehouseStockResponse>(`warehouses/${warehouseId}/stock`)

    if (resp.status === HttpStatusCode.NotFound) {
      return []
    }

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }

    return resp.data
  }

  public static async addContainers(
    warehouseId: number,
    containerTypeId: string,
    amount: number
  ): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.post('containers/', {
      body: {
        warehouseId,
        containers: [{ containerTypeId, amount }],
      },
    })

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }
  }

  public static async removeContainers(
    warehouseId: number,
    containerTypeId: string,
    amount: number
  ): Promise<void> {
    if (IS_LOCAL) return null

    const resp = await this.http.del('containers/', {
      body: {
        warehouseId,
        containers: [{ containerTypeId, amount }],
      },
    })

    if (resp.status !== 200) {
      throw new Error(resp.error)
    }
  }
}
